import { useState, useEffect, useMemo } from "react"
import {
  Search,
  Filter,
  Download,
  Calendar,
  DollarSign,
  TrendingUp,
  Clock,
  CheckCircle,
  XCircle,
  Eye,
  FileText,
  BarChart3,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Table2,
  PieChart as PieChartIcon,
  ChevronDown,
} from "lucide-react"
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'
import type { SalesReport } from "@/features/agents"
import { AgentService } from "@/features/agents"
import { apiService, API_ENDPOINTS } from "@/shared/services/api"
import type { APIResponse } from "@/shared/types"

type ViewMode = 'table' | 'chart'

interface FilterState {
  status?: string
  startDate?: string
  endDate?: string
  campaignId?: string
  searchTerm?: string
  timePeriod?: string
}

interface PaginationState {
  currentPage: number
  itemsPerPage: number
  totalItems: number
  totalPages: number
}

export default function AgentSalesReports() {
  const [reports, setReports] = useState<SalesReport[]>([])
  const [loading, setLoading] = useState(false)
  const [filters, setFilters] = useState<FilterState>({})
  const [showFilters, setShowFilters] = useState(false)
  const [showExportDropdown, setShowExportDropdown] = useState(false)
  const [selectedReport, setSelectedReport] = useState<SalesReport | null>(null)
  const [showDetails, setShowDetails] = useState(false)
  const [viewMode, setViewMode] = useState<ViewMode>('table')
  const [pagination, setPagination] = useState<PaginationState>({
    currentPage: 1,
    itemsPerPage: 10,
    totalItems: 0,
    totalPages: 0,
  })

  // Mock data for demonstration - expanded dataset
  const mockReports: SalesReport[] = []

  useEffect(() => {
    loadReports()
  }, [pagination.currentPage, pagination.itemsPerPage, filters.timePeriod])

  const loadReports = async () => {
    setLoading(true)
    try {
      const apiFilters = {
        startDate: filters.startDate,
        endDate: filters.endDate,
        status: filters.status,
        timePeriod: filters.timePeriod,
        search: filters.searchTerm,
        page: pagination.currentPage,
        limit: pagination.itemsPerPage
      }

      // Explicitly fetch from AgentService which now supports pagination
      const response = await apiService.get<APIResponse<{ reports: SalesReport[], total: number, page: number }>>(
        `${API_ENDPOINTS.agent.salesReports}?${new URLSearchParams(Object.entries(apiFilters).filter(([_, v]) => v !== undefined).map(([k, v]) => [k === 'search' ? 'searchTerm' : k, v!.toString()]))}`
      );

      const reportsData = response.data.data?.reports || []
      const totalItems = response.data.data?.total || 0

      setReports(reportsData)

      setPagination((prev) => ({
        ...prev,
        totalItems: totalItems,
        totalPages: Math.ceil(totalItems / prev.itemsPerPage),
      }))
    } catch (error) {
      console.error("Failed to load sales reports:", error)
      // Fallback to empty array on error
      setReports([])
      setPagination((prev) => ({
        ...prev,
        totalItems: 0,
        totalPages: 0,
      }))
    } finally {
      setLoading(false)
    }
  }

  const getFilteredReports = (allReports: SalesReport[]) => {
    return allReports.filter((report) => {
      if (filters.status && report.status !== filters.status) return false
      if (filters.startDate && report.reportDate < filters.startDate) return false
      if (filters.endDate && report.reportDate > filters.endDate) return false
      if (filters.campaignId && !report.campaignId.toLowerCase().includes(filters.campaignId.toLowerCase()))
        return false
      if (filters.searchTerm &&
        !report.legalGuardianFullName.toLowerCase().includes(filters.searchTerm.toLowerCase()) &&
        !report.videoGamerFullName.toLowerCase().includes(filters.searchTerm.toLowerCase()))
        return false
      return true
    })
  }

  const filteredReports = reports // Already filtered by backend

  // Get paginated ranges for display
  const startIndex = (pagination.currentPage - 1) * pagination.itemsPerPage
  const endIndex = startIndex + reports.length

  // Pagination helpers
  const totalPages = pagination.totalPages
  const currentPage = pagination.currentPage
  const pages = Array.from({ length: totalPages }, (_, i) => i + 1)

  // Paginated data is already returned by backend
  const paginatedReports = reports

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "APPROVED":
        return <CheckCircle className="w-4 h-4 text-green-600" />
      case "REJECTED":
        return <XCircle className="w-4 h-4 text-red-600" />
      case "PENDING":
        return <Clock className="w-4 h-4 text-yellow-600" />
      default:
        return <Clock className="w-4 h-4 text-accent-400" />
    }
  }

  const getStatusBadge = (status: string) => {
    const baseClasses = "inline-flex px-2 py-1 text-xs font-semibold rounded-full"
    switch (status) {
      case "APPROVED":
        return `${baseClasses} bg-green-100 text-green-800`
      case "REJECTED":
        return `${baseClasses} bg-red-100 text-red-800`
      case "PENDING":
        return `${baseClasses} bg-yellow-100 text-yellow-800`
      default:
        return `${baseClasses} bg-accent-100 text-accent-800`
    }
  }

  const exportReports = async (format: "csv" | "pdf" = "csv") => {
    try {
      const queryParams = new URLSearchParams()
      if (filters.status) queryParams.append("status", filters.status)
      if (filters.startDate) queryParams.append("dateFrom", filters.startDate)
      if (filters.endDate) queryParams.append("dateTo", filters.endDate)
      if (filters.campaignId) queryParams.append("campaignId", filters.campaignId)
      if (filters.timePeriod) queryParams.append("timePeriod", filters.timePeriod)
      if (filters.searchTerm) queryParams.append("searchTerm", filters.searchTerm)
      queryParams.append("format", format)

      const response = await apiService.get(`/sales-reports/export?${queryParams.toString()}`, {
        responseType: 'blob'
      })

      const blob = new Blob([response.data], { type: format === 'pdf' ? 'application/pdf' : 'text/csv' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.href = url
      link.setAttribute("download", `sales_reports_${new Date().toISOString().split("T")[0]}.${format}`)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error("Failed to export reports:", error)
      alert("Failed to export reports. Please try again.")
    }
  }

  const viewDetails = (report: SalesReport) => {
    setSelectedReport(report)
    setShowDetails(true)
  }

  const handlePageChange = (page: number) => {
    setPagination((prev) => ({ ...prev, currentPage: page }))
  }

  const handleItemsPerPageChange = (itemsPerPage: number) => {
    setPagination((prev) => ({
      ...prev,
      itemsPerPage,
      currentPage: 1,
      totalPages: Math.ceil(filteredReports.length / itemsPerPage),
    }))
  }

  const applyFilters = () => {
    loadReports()
    setPagination((prev) => ({
      ...prev,
      currentPage: 1,
    }))
  }

  // Calculate summary statistics
  const totalSales = filteredReports.length
  const approvedSales = filteredReports.filter((r) => r.status === "APPROVED").length
  const pendingCount = filteredReports.filter((r) => r.status === "PENDING").length
  const approvedCount = filteredReports.filter((r) => r.status === "APPROVED").length
  const rejectedCount = filteredReports.filter((r) => (r.status as string) === "REJECTED").length

  // Chart colors
  const STATUS_COLORS = {
    PENDING: '#f59e0b',
    APPROVED: '#10b981',
    REJECTED: '#ef4444',
  }

  const CAMPAIGN_COLORS = ['#3b82f6', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16']

  // Prepare chart data
  const statusChartData = useMemo(() => {
    return [
      { name: 'Pending', value: pendingCount, fill: STATUS_COLORS.PENDING },
      { name: 'Approved', value: approvedCount, fill: STATUS_COLORS.APPROVED },
      { name: 'Rejected', value: rejectedCount, fill: STATUS_COLORS.REJECTED },
    ].filter(item => item.value > 0)
  }, [pendingCount, approvedCount, rejectedCount])

  const trendChartData = useMemo(() => {
    const grouped = new Map<string, { sales: number; approved: number }>()

    filteredReports.forEach(report => {
      const date = new Date(report.reportDate).toISOString().split('T')[0]
      const existing = grouped.get(date) || { sales: 0, approved: 0 }
      grouped.set(date, {
        sales: existing.sales + 1,
        approved: existing.approved + (report.status === 'APPROVED' ? 1 : 0),
      })
    })

    return Array.from(grouped.entries())
      .map(([date, data]) => ({ date, ...data }))
      .sort((a, b) => a.date.localeCompare(b.date))
  }, [filteredReports])

  const campaignChartData = useMemo(() => {
    const grouped = new Map<string, number>()

    filteredReports.forEach(report => {
      const campaign = report.campaignName || 'Unknown'
      grouped.set(campaign, (grouped.get(campaign) || 0) + 1)
    })

    return Array.from(grouped.entries())
      .map(([name, count], index) => ({
        name,
        count,
        fill: CAMPAIGN_COLORS[index % CAMPAIGN_COLORS.length],
      }))
      .sort((a, b) => b.count - a.count)
  }, [filteredReports])

  // Pagination component
  const PaginationControls = () => {
    const { currentPage, totalPages } = pagination
    const maxVisiblePages = 5

    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2))
    const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1)

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1)
    }

    const pages = Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i)

    return (
      <div className="flex items-center justify-between px-4 py-3 bg-white border-t border-accent-200 sm:px-6">
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center space-x-2">
            <span className="text-sm text-accent-700">Show</span>
            <select
              value={pagination.itemsPerPage}
              onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
              className="border border-accent-300 rounded px-2 py-1 text-sm"
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
            <span className="text-sm text-accent-700">per page</span>
          </div>

          <div className="flex items-center space-x-2">
            <span className="text-sm text-accent-700">
              Showing {pagination.totalItems > 0 ? startIndex + 1 : 0} to {endIndex} of {pagination.totalItems}{" "}
              results
            </span>
          </div>

          <div className="flex items-center space-x-1">
            {/* First page */}
            <button
              onClick={() => handlePageChange(1)}
              disabled={currentPage === 1}
              className="p-2 text-accent-500 hover:text-accent-700 disabled:text-accent-300 disabled:cursor-not-allowed"
            >
              <ChevronsLeft className="w-4 h-4" />
            </button>

            {/* Previous page */}
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="p-2 text-accent-500 hover:text-accent-700 disabled:text-accent-300 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>

            {/* Page numbers */}
            {pages.map((page) => (
              <button
                key={page}
                onClick={() => handlePageChange(page)}
                className={`px-3 py-1 text-sm rounded ${page === currentPage ? "bg-primary-600 text-white" : "text-accent-700 hover:bg-accent-100"
                  }`}
              >
                {page}
              </button>
            ))}

            {/* Next page */}
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="p-2 text-accent-500 hover:text-accent-700 disabled:text-accent-300 disabled:cursor-not-allowed"
            >
              <ChevronRight className="w-4 h-4" />
            </button>

            {/* Last page */}
            <button
              onClick={() => handlePageChange(totalPages)}
              disabled={currentPage === totalPages}
              className="p-2 text-accent-500 hover:text-accent-700 disabled:text-accent-300 disabled:cursor-not-allowed"
            >
              <ChevronsRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-accent-900">My Sales Reports</h2>
          <p className="text-accent-600">Track your sales performance and commission reports</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 items-center">
          {/* Time Period Filter - Always Visible */}
          <div className="flex items-center">
            <select
              className="input-field py-1"
              value={filters.timePeriod || "all"}
              onChange={(e) => setFilters({ ...filters, timePeriod: e.target.value })}
            >
              <option value="day">Today</option>
              <option value="week">This Week</option>
              <option value="month">This Month</option>
              <option value="all">All</option>
            </select>
          </div>

          {/* View Toggle */}
          <div className="inline-flex rounded-lg border border-accent-300 p-1 bg-accent-50">
            <button
              onClick={() => setViewMode('table')}
              className={`flex items-center space-x-1 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${viewMode === 'table'
                ? 'bg-primary-600 text-white shadow-sm'
                : 'text-accent-700 hover:bg-accent-100'
                }`}
            >
              <Table2 className="w-4 h-4" />
              <span>Table</span>
            </button>
            <button
              onClick={() => setViewMode('chart')}
              className={`flex items-center space-x-1 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${viewMode === 'chart'
                ? 'bg-primary-600 text-white shadow-sm'
                : 'text-accent-700 hover:bg-accent-100'
                }`}
            >
              <PieChartIcon className="w-4 h-4" />
              <span>Charts</span>
            </button>
          </div>

          {/* Export Dropdown */}
          <div className="relative">
            <button
              onClick={() => setShowExportDropdown(!showExportDropdown)}
              className="btn-primary flex items-center"
            >
              <Download className="w-4 h-4 mr-2" />
              Export
              <ChevronDown className="w-4 h-4 ml-2" />
            </button>

            {showExportDropdown && (
              <div className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50">
                <div className="py-1" role="menu" aria-orientation="vertical">
                  <button
                    onClick={() => {
                      exportReports("csv")
                      setShowExportDropdown(false)
                    }}
                    className="flex items-center px-4 py-2 text-sm text-accent-700 hover:bg-accent-100 w-full text-left"
                    role="menuitem"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download CSV
                  </button>
                  <button
                    onClick={() => {
                      exportReports("pdf")
                      setShowExportDropdown(false)
                    }}
                    className="flex items-center px-4 py-2 text-sm text-accent-700 hover:bg-accent-100 w-full text-left"
                    role="menuitem"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    Download PDF
                  </button>
                </div>
              </div>
            )}
          </div>

          <button
            onClick={() => setShowFilters(!showFilters)}
            className="btn-outline flex items-center justify-center space-x-2"
          >
            <Filter className="w-4 h-4" />
            <span>More Filters</span>
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-accent-600">Total Sales</p>
              <p className="text-2xl font-bold text-accent-900">{totalSales}</p>
            </div>
            <div className="w-12 h-12 bg-secondary-100 rounded-lg flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-secondary-600" />
            </div>
          </div>
        </div>
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-accent-600">Approved Sales</p>
              <p className="text-2xl font-bold text-green-600">{approvedSales}</p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-accent-600">In Progress</p>
              <p className="text-2xl font-bold text-yellow-600">{pendingCount}</p>
            </div>
            <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
              <Clock className="w-6 h-6 text-yellow-600" />
            </div>
          </div>
        </div>
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-accent-600">Failed Sales</p>
              <p className="text-2xl font-bold text-red-600">{rejectedCount}</p>
            </div>
            <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
              <XCircle className="w-6 h-6 text-red-600" />
            </div>
          </div>
        </div>
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-accent-600">Success Rate</p>
              <p className="text-2xl font-bold text-primary-600">
                {filteredReports.length > 0 ? Math.round((approvedCount / filteredReports.length) * 100) : 0}%
              </p>
            </div>
            <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-primary-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="card">
          <h3 className="text-lg font-semibold text-accent-900 mb-4">Filter Reports</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-accent-700 mb-1">Status</label>
              <select
                className="input-field"
                value={filters.status || ""}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              >
                <option value="">All Status</option>
                <option value="PENDING">In Progress</option>
                <option value="APPROVED">Approved</option>
                <option value="REJECTED">Failed</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-accent-700 mb-1">Campaign</label>
              <input
                type="text"
                className="input-field"
                placeholder="Campaign ID"
                value={filters.campaignId || ""}
                onChange={(e) => setFilters({ ...filters, campaignId: e.target.value })}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-accent-700 mb-1">Start Date</label>
              <input
                type="date"
                className="input-field"
                value={filters.startDate || ""}
                onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-accent-700 mb-1">End Date</label>
              <input
                type="date"
                className="input-field"
                value={filters.endDate || ""}
                onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
              />
            </div>
          </div>
          <div className="mt-4 flex justify-end space-x-2">
            <button onClick={() => setFilters({})} className="btn-outline">
              Clear Filters
            </button>
            <button onClick={applyFilters} className="btn-primary">
              Apply Filters
            </button>
          </div>
        </div>
      )}

      {/* Search Bar */}
      <div className="card">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-accent-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Search by client name..."
            className="input-field pl-10"
            value={filters.searchTerm || ""}
            onChange={(e) => {
              setFilters({ ...filters, searchTerm: e.target.value })
              // Auto-apply search filter
              setTimeout(applyFilters, 300)
            }}
          />
        </div>
      </div>

      {/* Reports Content - Table or Charts based on viewMode */}
      {viewMode === 'table' ? (
        /* Table View */
        <div className="card p-0">
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
              <p className="mt-2 text-accent-600">Loading reports...</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-accent-200">
                  <thead className="bg-accent-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-accent-500 uppercase tracking-wider">
                        Client Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-accent-500 uppercase tracking-wider">
                        Campaign
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-accent-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-accent-500 uppercase tracking-wider">
                        Report Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-accent-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-accent-200">
                    {paginatedReports.map((report) => (
                      <tr key={report.id} className="hover:bg-accent-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center mr-3">
                              <FileText className="w-4 h-4 text-primary-600" />
                            </div>
                            <div className="text-sm font-medium text-accent-900">
                              <div>{report.legalGuardianFullName}</div>
                              <div className="text-xs text-accent-600">{report.videoGamerFullName}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-accent-900">{report.campaignName}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center space-x-2">
                            {getStatusIcon(report.status)}
                            <span className={getStatusBadge(report.status)}>{report.status}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center space-x-1 text-sm text-accent-500">
                            <Calendar className="w-4 h-4" />
                            <span>{new Date(report.reportDate).toLocaleDateString()}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <button
                            onClick={() => viewDetails(report)}
                            className="text-primary-600 hover:text-primary-900 flex items-center space-x-1"
                          >
                            <Eye className="w-4 h-4" />
                            <span>View</span>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {paginatedReports.length === 0 && !loading && (
                  <div className="text-center py-8">
                    <BarChart3 className="w-12 h-12 text-accent-300 mx-auto mb-4" />
                    <p className="text-accent-500">No sales reports found matching your criteria.</p>
                    <p className="text-sm text-accent-400 mt-1">Try adjusting your filters or search terms.</p>
                  </div>
                )}
              </div>

              {/* Pagination Controls */}
              {filteredReports.length > 0 && <PaginationControls />}
            </>
          )}
        </div>
      ) : (
        /* Chart View */
        <div className="space-y-6">
          {loading ? (
            <div className="card text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
              <p className="mt-2 text-accent-600">Loading charts...</p>
            </div>
          ) : filteredReports.length === 0 ? (
            <div className="card text-center py-12">
              <BarChart3 className="w-16 h-16 text-accent-300 mx-auto mb-4" />
              <p className="text-accent-500 text-lg">No data available for charts</p>
              <p className="text-sm text-accent-400 mt-1">Try adjusting your filters or add some sales reports.</p>
            </div>
          ) : (
            <>
              {/* Status Distribution Chart (Pie Chart) */}
              <div className="card">
                <h3 className="text-lg font-semibold mb-4 text-accent-900">Sales by Status</h3>
                {statusChartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={statusChartData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name}: ${((percent || 0) * 100).toFixed(0)}%`}
                        outerRadius={100}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {statusChartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.fill} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[300px] flex items-center justify-center text-accent-500">
                    No status data available
                  </div>
                )}
              </div>

              {/* Sales Trend Chart (Line Chart) */}
              <div className="card">
                <h3 className="text-lg font-semibold mb-4 text-accent-900">Sales Trend Over Time</h3>
                {trendChartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={trendChartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="date"
                        tick={{ fontSize: 12 }}
                        tickFormatter={(date) => new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      />
                      <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                      <Tooltip
                        labelFormatter={(label) => new Date(label as string).toLocaleDateString()}
                        formatter={(value: any, name: any) => [value ?? 0, name === 'sales' ? 'Total Sales' : 'Approved']}
                      />
                      <Legend />
                      <Line
                        type="monotone"
                        dataKey="sales"
                        stroke="#3b82f6"
                        strokeWidth={2}
                        name="Total Sales"
                        dot={{ r: 4 }}
                        activeDot={{ r: 6 }}
                      />
                      <Line
                        type="monotone"
                        dataKey="approved"
                        stroke="#10b981"
                        strokeWidth={2}
                        name="Approved"
                        dot={{ r: 4 }}
                        activeDot={{ r: 6 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[300px] flex items-center justify-center text-accent-500">
                    No trend data available
                  </div>
                )}
              </div>

              {/* Campaign Breakdown Chart (Bar Chart) */}
              <div className="card">
                <h3 className="text-lg font-semibold mb-4 text-accent-900">Sales by Campaign</h3>
                {campaignChartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={campaignChartData} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12 }} />
                      <YAxis
                        dataKey="name"
                        type="category"
                        tick={{ fontSize: 12 }}
                        width={150}
                      />
                      <Tooltip />
                      <Bar dataKey="count" name="Sales Count" radius={[0, 4, 4, 0]}>
                        {campaignChartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.fill} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[300px] flex items-center justify-center text-accent-500">
                    No campaign data available
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {/* Report Details Modal */}
      {showDetails && selectedReport && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-semibold text-accent-900">Sales Report Details</h3>
              <button className="text-accent-500 hover:text-accent-700" onClick={() => setShowDetails(false)}>
                <XCircle className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-3">
                  <div>
                    <label className="text-sm font-medium text-accent-600">Report ID</label>
                    <p className="text-accent-900 font-mono">{selectedReport.id}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-accent-600">Legal Guardian</label>
                    <p className="text-accent-900">{selectedReport.legalGuardianFullName}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-accent-600">Video Gamer</label>
                    <p className="text-accent-900">{selectedReport.videoGamerFullName}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-accent-600">Campaign</label>
                    <p className="text-accent-900">{selectedReport.campaignName}</p>
                  </div>
                </div>

                <div className="space-y-3">
                  <div>
                    <label className="text-sm font-medium text-accent-600">Status</label>
                    <div className="flex items-center space-x-2 mt-1">
                      {getStatusIcon(selectedReport.status)}
                      <span className={getStatusBadge(selectedReport.status)}>{selectedReport.status}</span>
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-accent-600">Report Date</label>
                    <p className="text-accent-900">{new Date(selectedReport.reportDate).toLocaleDateString()}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-accent-600">Created Date</label>
                    <p className="text-accent-900">{new Date(selectedReport.createdAt).toLocaleString()}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-accent-600">Last Updated</label>
                    <p className="text-accent-900">{new Date(selectedReport.updatedAt).toLocaleString()}</p>
                  </div>
                </div>
              </div>

              {/* {selectedReport.status === "REJECTED" && (
                <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
                  <h4 className="text-sm font-medium text-red-800 mb-2">Rejection Reason</h4>
                  <p className="text-sm text-red-700">
                    This report was rejected due to incomplete documentation. Please ensure all required fields are
                    properly filled and supporting documents are attached.
                  </p>
                </div>
              )} */}

              {selectedReport.status === "PENDING" && (
                <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
                  <h4 className="text-sm font-medium text-yellow-800 mb-2">Pending Review</h4>
                  <p className="text-sm text-yellow-700">
                    This report is currently under review by the HR team. You will be notified once the review is
                    complete.
                  </p>
                </div>
              )}
            </div>

            <div className="mt-6 flex justify-end space-x-2">
              <button className="btn-outline" onClick={() => setShowDetails(false)}>
                Close
              </button>
              <button className="btn-primary">Download PDF</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
